import { Lock, LockOpen } from "lucide-react";
import { type ReactNode, useState } from "react";
import { basisLabel, rateFor } from "../../shared/money";
import { useConfirm } from "../../web/components/Confirm";
import type { WindowAction } from "../../web/components/RecordWindow";
import { ErrorNotice, Notice } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { can, itemName, useHistory, usePriceListSaver } from "./data";
import { lockText, type MaterialRow, type PriceListData, type PriceLock, priceLock } from "./meta";

/*
 * Price locks on screen (locks.ts): locking and unlocking (a window's More menu), the question a
 * locked price's change asks first, its mark, and the warning when it was changed in ServiceM8.
 * Locking, unlocking and changing a locked price are each held to confirm (Confirm.tsx hold).
 */

/* Locking and unlocking say the same thing, flipped: whether changing a price or tax needs an extra confirmation. */

/** An item's lock in its More menu: Lock price, or Unlock price. Locked by its group, it's unlocked there (shown, off). */
export function itemLockAction(data: PriceListData, item: MaterialRow, onChange: (locked: boolean) => void): WindowAction {
    const lock = priceLock(data, item.uuid);
    if (lock?.by === "item") {
        return {
            icon: <LockOpen className="size-4" />,
            label: "Unlock price",
            help: "Its price and tax can change without confirming.",
            confirm: {
                title: "Unlock this item's price?",
                message: "Price and tax changes won't need confirming.",
                confirmLabel: "Unlock price",
                hold: true,
            },
            run: () => onChange(false),
        };
    }
    const byGroup = lock?.by === "group" ? lock.group : null;
    return {
        icon: <Lock className="size-4" />,
        label: "Lock price",
        help: byGroup ? `Already locked by the ${byGroup} group (unlocked there).` : "Its price and tax only change once confirmed, e.g. for a price on your website.",
        disabled: byGroup !== null,
        confirm: {
            title: "Lock this item's price?",
            message: "Price and tax changes will need confirming.",
            confirmLabel: "Lock price",
            hold: true,
        },
        run: () => onChange(true),
    };
}

/** A group's lock in its More menu: Lock prices (every item in it), or Unlock prices. */
export function groupLockAction(name: string, locked: boolean, onChange: (locked: boolean) => void): WindowAction {
    if (locked) {
        return {
            icon: <LockOpen className="size-4" />,
            label: "Unlock prices",
            help: "Its items' prices and tax can change without confirming.",
            confirm: {
                title: `Unlock the ${name} group's prices?`,
                message: "Its items' price and tax changes won't need confirming.",
                confirmLabel: "Unlock prices",
                hold: true,
            },
            run: () => onChange(false),
        };
    }
    return {
        icon: <Lock className="size-4" />,
        label: "Lock prices",
        help: "Its items' prices and tax only change once confirmed, e.g. for prices on your website.",
        confirm: {
            title: `Lock the ${name} group's prices?`,
            message: "Its items' price and tax changes will need confirming.",
            confirmLabel: "Lock prices",
            hold: true,
        },
        run: () => onChange(true),
    };
}

/**
 * Before changing a locked item's price or tax: asks, naming the lock and the change ("$160.00 ->
 * $170.00 inc GST"), its button pressed and held to go ahead. "confirmed": asked, and yes (tell the
 * server: confirmLocked); "unlocked": not locked, so not asked (don't: if it was locked meanwhile,
 * the server refuses); "cancelled": no.
 */
export type LockedAnswer = "confirmed" | "unlocked" | "cancelled";

export function useLockedPriceConfirm(data: PriceListData, opts: { named?: boolean } = {}) {
    const confirm = useConfirm();
    /** `change`: what changes, as its save bar says it (ChangeLines). */
    return async (item: MaterialRow, change: ReactNode): Promise<LockedAnswer> => {
        const lock = priceLock(data, item.uuid);
        if (!lock) return "unlocked";
        const yes = await confirm({
            title: "Change a locked price?",
            // Asked in its own window, it needn't be named; from a list of items (a group's), it is.
            subject: opts.named ? itemName(data, item) : undefined,
            message: change,
            detail: `${lockText(lock)}.`,
            confirmLabel: "Change locked price",
            // A deliberate change, not a loss: green, made weighty by holding it.
            tone: "primary",
            // Held, not clicked: it can't go ahead by a reflex click or Enter.
            hold: true,
        });
        return yes ? "confirmed" : "cancelled";
    };
}

/**
 * What a save changes, as its save bar and its question both say it: "Price: $133.00 → $146.00", and under it "Cost: $41.80 →
 * $87.26" (each only when it changes; with its tax, `label`, when that changes too).
 */
export function ChangeLines(
    props: Readonly<{ price?: { from: string; to: string; fromLabel?: string; toLabel?: string }; cost?: { from: string; to: string; label?: string } }>,
) {
    const fmt = useMoney();
    const line = (what: string, from: string, to: string, fromLabel?: string, toLabel?: string) => (
        <span className="block">
            {what}: <span className="tabular-nums">{fmt.money(from)}</span>
            {fromLabel ? ` ${fromLabel}` : ""} → <span className="font-semibold text-ink tabular-nums">{fmt.money(to)}</span>
            {toLabel ? ` ${toLabel}` : ""}
        </span>
    );
    return (
        <>
            {props.price ? line("Price", props.price.from, props.price.to, props.price.fromLabel, props.price.toLabel) : null}
            {props.cost ? line("Cost", props.cost.from, props.cost.to, props.cost.label, props.cost.label) : null}
        </>
    );
}

/** The lock's mark: a small lock, its words on hover (or beside it, `withText`). Nothing when it isn't locked. */
export function LockMark({ lock, withText }: Readonly<{ lock: PriceLock | null; withText?: boolean }>) {
    if (!lock) return null;
    return (
        <span className="inline-flex shrink-0 items-center gap-1 text-muted" title={lockText(lock)}>
            <Lock className="size-3.5" aria-label={withText ? undefined : lockText(lock)} />
            {withText ? <span className="text-xs">{lockText(lock)}</span> : null}
        </span>
    );
}

/**
 * Its locked price or tax was changed in ServiceM8 (not seen yet): red, with the price it had and
 * has, to put back (to ServiceM8) or keep. In its window's warnings.
 */
export function LockedChangeNotice({ data, item }: Readonly<{ data: PriceListData; item: MaterialRow }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const history = useHistory("material", item.uuid);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const lock = priceLock(data, item.uuid);
    if (!lock) return null;
    // Its price before the first of the changes not seen yet (from its history: oldest first).
    const unseen = (history.data ?? []).filter((c) => c.source === "servicem8" && !c.seen).reverse();
    const priceBefore = unseen.find((c) => c.field === "price")?.before;
    const basis = basisLabel(item.price_includes_taxes, rateFor(item.tax_rate_uuid, data.taxRates));
    const run = (fn: () => Promise<unknown>) => {
        setBusy(true);
        setError(null);
        fn()
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    const canEdit = can(data, "items.pricing");
    return (
        <Notice
            tone="error"
            title="Locked price changed in ServiceM8."
            actions={
                canEdit ? (
                    <>
                        <button type="button" className="btn-primary" disabled={busy} onClick={() => run(() => saver.putBackPrice(item.uuid))}>
                            Put back{typeof priceBefore === "string" ? ` ${fmt.money(priceBefore)}` : ""}
                        </button>
                        <button type="button" className="btn-secondary" disabled={busy} title="Keep the change: it's the price now" onClick={() => run(() => saver.keepPriceChange(item.uuid))}>
                            Keep {fmt.money(item.price)}
                        </button>
                    </>
                ) : null
            }
        >
            {lockText(lock)}, but its price or tax was changed in ServiceM8: it&apos;s {fmt.money(item.price)} {basis} now
            {typeof priceBefore === "string" ? `, was ${fmt.money(priceBefore)}` : ""}.
            <ErrorNotice error={error} />
        </Notice>
    );
}
